import {Component, DestroyRef, inject, Inject, OnInit, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {trigger, state, transition, animate, style} from '@angular/animations';
import {map, first} from 'rxjs/operators';
import {ISearchableOption} from '@emm/core/modules/cfa-data-table/models/ISearchOption';
import { CfaDataTable } from '@emm/core/modules/cfa-data-table/cfa-data-table.component';
import {IDataTableColumn, IEpochColumn} from '@emm/core/modules/cfa-data-table/models/IDataColumn';
import {IFilterOption} from '@emm/core/modules/cfa-data-table/models/IFilterOption';
import {Observable} from 'rxjs';
import * as dayjs from 'dayjs';
import * as R from 'ramda';
import {RegexConstants} from '@emm/core/util/regex.constants';
import {MasterMenuSystemService} from '@emm/core/services/master-menu-system.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {Revotes} from '@emm/core/models/revotes.model';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
    selector: 'emm-add-revotes-dialog',
    templateUrl: './add-revotes-dialog.component.html',
    styleUrls: ['./add-revotes-dialog.component.scss'],
    animations: [
        trigger('shrinkForTimeline', [
            state('normal', style({paddingLeft: '0px', paddingRight: '0px', paddingTop: '0px'})),
            state('timeline', style({paddingLeft: '125px', paddingRight: '0px', paddingTop: '0px'})),
            state('filter', style({paddingLeft: '0px', paddingRight: 'min(240px, 75%)', paddingTop: '0px'})),
            state('search', style({paddingLeft: '0px', paddingRight: '0px', paddingTop: '80px'})),
            transition('* => timeline', animate('120ms cubic-bezier(0, 0, 0.2, 1)')),
            transition('* => filter', animate('120ms cubic-bezier(0, 0, 0.2, 1)')),
            transition('* => normal', animate('120ms cubic-bezier(0, 0, 0.2, 1)')),
            transition('* => search', animate('120ms cubic-bezier(0, 0, 0.2, 1)'))
        ]),
        trigger('slideInTimeline', [
            state('inactive', style({opacity: '0', transform: 'translateX(-100%)'})),
            state('active', style({opacity: '1', transform: 'none'})),
            transition('inactive => active', animate('120ms cubic-bezier(0, 0, 0.2, 1)')),
            transition('active => inactive', animate('120ms cubic-bezier(0, 0, 0.2, 1)'))
        ]),
        trigger('slideInFilters', [
            state('inactive', style({opacity: '0', transform: 'translateX(100%)'})),
            state('active', style({opacity: '1', transform: 'none'})),
            transition('inactive => active', animate('120ms cubic-bezier(0, 0, 0.2, 1)')),
            transition('active => inactive', animate('120ms cubic-bezier(0, 0, 0.2, 1)'))
        ]),
        trigger('fadeInSearch', [
            state('void', style({opacity: '0'})),
            state('active', style({opacity: '1'})),
            transition('void => active', animate('120ms cubic-bezier(0, 0, 0.2, 1)')),
            transition('active => void', animate('120ms cubic-bezier(0, 0, 0.2, 1)'))
        ])
    ]
})
export class AddRevotesDialogComponent implements OnInit {
    @ViewChild(CfaDataTable, {static: true}) dataTable: CfaDataTable<any>;
    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    public isLinear = true;
    private locationPaneFilter: IFilterOption = {
        columnLabel: 'Locations',
        columnProperty: 'countryCode',
        options: [],
        filtered: []
    };
    private locationTypesPaneFilter: IFilterOption = {
        columnLabel: 'Location Types',
        columnProperty: 'locationType',
        options: [],
        filtered: []
    };
    selectedConsumer = '';
    name: string;
    firstFormGroup: FormGroup;
    secondFormGroup: FormGroup;
    thirdFormGroup: FormGroup;
    fourthFormGroup: FormGroup;
    finalFormGroup: FormGroup;
    tomorrowDate: any;
    oneDayAfterCloseDate: any;
    closeMinDate: any;
    locations = this.data.locations;
    categories = this.data.categories;
    selectedCategoriesTags = [];
    selectedCategories = [];
    selectedLocations = [];
    spaceValidatorPattern = RegexConstants.STARTING_SPACE_VALIDATIOR_PATTERN;
    filterOptions: IFilterOption[] = [
        this.locationPaneFilter,
        this.locationTypesPaneFilter
    ];

    searchableOptions: ISearchableOption[] = [
        {columnLabel: 'All Columns', columnProperty: null}
    ];
    dataColumns: IDataTableColumn[] = [
        {
            label: 'Name',
            property: 'name',
            width: '200px'
        },
        {
            label: 'Number',
            property: 'tag',
            width: '80px'
        },
        {
            label: 'Country',
            property: 'countryCode',
            width: '80px'
        },
        {
            label: 'Location Type',
            property: 'locationType',
            width: '110px'
        }
    ];

    epochColumn: IEpochColumn = {
        label: 'Date/Time',
        sortProperty: 'audit_timestamp',
        displayProperty: 'audit_timestamp_string',
        width: '180px'
    };

    constructor(
        public dialogRef: MatDialogRef<AddRevotesDialogComponent>,
        private fb: FormBuilder,
        private masterMenuService: MasterMenuSystemService,
        private notificationService: NotificationService,
        @Inject(MAT_DIALOG_DATA) public data: any) {
    }

    ngOnInit() {
        this.dialogRef.updatePosition({top: '100px'});
        this.dialogRef.updateSize('auto');
        this.firstFormGroup = this.fb.group({
            name: ['', Validators.required]
        });
        this.secondFormGroup = this.fb.group({
            openDate: ['', Validators.required],
            closeDate: ['', Validators.required],
            effectiveDate: ['', Validators.required]
        });
        this.thirdFormGroup = this.fb.group({
            categories: ['', Validators.required]
        });
        this.finalFormGroup = this.fb.group({
            selectedLocations: ['']
        });
        const today = dayjs(new Date());
        this.tomorrowDate = today.add(1, 'day').format();
    }

    getData(end: number, skipCache?: boolean): Observable<any[]> {
        if (skipCache) {
            this.locationPaneFilter.options = [];
            this.locationTypesPaneFilter.options = [];
        }
        return this.locations.pipe(
            first(),
            map(locations => {
                // @ts-ignore
                locations.forEach(log => {
                    // console.log(log);
                    if (!this.locationPaneFilter.options.includes(log.countryCode)) {
                        this.locationPaneFilter.options.push(log.countryCode);
                    }
                    if (!this.locationTypesPaneFilter.options.includes(log.subTypeCode)) {
                        this.locationTypesPaneFilter.options.push(log.subTypeCode);
                    }
                });
                this.locationPaneFilter.options = this.locationPaneFilter.options.sort((a, b) => a.localeCompare(b));
                // @ts-ignore
                return locations.map(
                    location => ({
                        countryCode: location.countryCode,
                        description: location.description,
                        name: location.name,
                        scope: location.scope,
                        tag: location.tag,
                        id: location.tag,
                        locationType: location.subTypeCode,
                    })
                );
            }));
    }

    getSelectedLocations(selected) {
        this.finalFormGroup.get('selectedLocations').setValue(selected);
        this.selectedLocations.push(selected);
    }

    public onNoClick(): void {
        this.dialogRef.close();
    }

    selectEffectiveDate(date: string) {
        if (this.closeMinDate !== date) {
            this.secondFormGroup.get('effectiveDate').setValue(date);
        } else {
            this.notificationService.error('End date must be later than the effective date');
        }
    }

    selectOpenDate(date: string) {
        this.secondFormGroup.get('openDate').setValue(date);
        this.closeMinDate = date;
    }

    selectCloseDate(date: string) {
        this.secondFormGroup.get('closeDate').setValue(date);
        this.oneDayAfterCloseDate = new Date(date);
        this.oneDayAfterCloseDate.setDate(this.oneDayAfterCloseDate.getDate() + 1);
    }

    updateAllSelected(checked, checkedValue) {
        if (checked) {
            this.selectedCategories.push(checkedValue);
        } else {
            const selectedIndex = R.findIndex(R.propEq('tag', checkedValue.tag))(this.selectedCategories);
            this.selectedCategories.splice(selectedIndex, 1);
        }
    }

    public closeAfterSave(): void {
        this.dialogRef.close('submit');
    }

    OnSubmit() {
        this.selectedCategories.forEach((category) => {
            this.selectedCategoriesTags.push(category.tag);
        });
        if (this.selectedCategories[0].tag.toString().includes('LIC')) {
            this.selectedConsumer = 'MMS_LICENSEE';
        } else {
            this.selectedConsumer = 'MENU_ALIGNMENT';
        }
        const revote: Revotes = {
            id: null,
            destination: this.selectedConsumer,
            name: this.firstFormGroup.get('name').value,
            effectiveDate: dayjs(this.secondFormGroup.get('effectiveDate').value).format('YYYY-MM-DD'),
            openDate: dayjs(this.secondFormGroup.get('openDate').value).format('YYYY-MM-DD'),
            closeDate: dayjs(this.secondFormGroup.get('closeDate').value).format('YYYY-MM-DD'),
            submenus: this.selectedCategoriesTags,
            locations: R.last(this.selectedLocations)
        };
        this.masterMenuService.saveRevote(revote)
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((res) => {
            this.closeAfterSave();
        });
    }
}
